import { useEffect, useState } from "react";
import { getSettings, saveSetting, logActivity } from "@/lib/cms";
import { useToast } from "@/context/ToastContext";
import { useSiteContent } from "@/context/SiteContentContext";
import {
  PageHeader,
  Button,
  Card,
  Spinner,
  Field,
  Input,
  Textarea,
} from "@/pages/admin/components/ui";
import ImageUploader from "@/pages/admin/components/ImageUploader";

export default function Marca() {
  const { success, error: toastError } = useToast();
  const { refresh } = useSiteContent();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    brand: "",
    tagline: "",
    secondary_tagline: "",
    brand_description: "",
    contact_email: "",
    whatsapp_number: "",
    whatsapp_message: "",
    logo_url: "",
    logo_dark_url: "",
    logo_mobile_url: "",
    favicon_url: "",
  });

  useEffect(() => {
    getSettings().then((s) => {
      setForm((f) => ({
        ...f,
        brand: String(s.brand ?? ""),
        tagline: String(s.tagline ?? ""),
        secondary_tagline: String(s.secondary_tagline ?? ""),
        brand_description: String(s.brand_description ?? ""),
        contact_email: String(s.contact_email ?? ""),
        whatsapp_number: String(s.whatsapp_number ?? ""),
        whatsapp_message: String(s.whatsapp_message ?? ""),
        logo_url: String(s.logo_url ?? ""),
        logo_dark_url: String(s.logo_dark_url ?? ""),
        logo_mobile_url: String(s.logo_mobile_url ?? ""),
        favicon_url: String(s.favicon_url ?? ""),
      }));
      setLoading(false);
    });
  }, []);

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function save() {
    setSaving(true);
    try {
      const entries = Object.entries(form) as [string, string][];
      for (const [key, value] of entries) {
        await saveSetting(key, value);
      }
      await logActivity("actualizó la identidad de marca");
      success("Identidad de marca guardada correctamente.");
      refresh();
    } catch {
      toastError("No se pudieron guardar los cambios.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Spinner label="Cargando identidad de marca…" />;

  return (
    <div>
      <PageHeader
        title="Identidad de marca"
        subtitle="Logo, favicon, nombre y datos de contacto."
        actions={<Button onClick={save} disabled={saving}>{saving ? "Guardando…" : "Guardar cambios"}</Button>}
      />

      <div className="space-y-6">
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4">Marca</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nombre de empresa"><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} /></Field>
            <Field label="Eslogan"><Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} /></Field>
            <Field label="Eslogan secundario"><Input value={form.secondary_tagline} onChange={(e) => set("secondary_tagline", e.target.value)} /></Field>
            <Field label="Descripción corta"><Input value={form.brand_description} onChange={(e) => set("brand_description", e.target.value)} /></Field>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4">Logos y favicon</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <ImageUploader label="Logo principal" value={form.logo_url} onChange={(v) => set("logo_url", v)} folder="marca" aspect="aspect-[16/6]" />
            <ImageUploader label="Logo para fondo oscuro" value={form.logo_dark_url} onChange={(v) => set("logo_dark_url", v)} folder="marca" aspect="aspect-[16/6]" />
            <ImageUploader label="Logo móvil" value={form.logo_mobile_url} onChange={(v) => set("logo_mobile_url", v)} folder="marca" aspect="aspect-square" />
            <ImageUploader label="Favicon" value={form.favicon_url} onChange={(v) => set("favicon_url", v)} folder="marca" aspect="aspect-square" />
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4">Contacto</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Email"><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></Field>
            <Field label="WhatsApp (solo números, con código de país)" hint="Ej. 18095551234"><Input value={form.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} /></Field>
            <div className="sm:col-span-2">
              <Field label="Mensaje predefinido de WhatsApp"><Textarea rows={2} value={form.whatsapp_message} onChange={(e) => set("whatsapp_message", e.target.value)} /></Field>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}